import { Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import ReactPaginate from 'react-paginate'

import Navbar from '../../components/navbar/navbar'
import { CustomInputDate } from '../../components/CustomInputDate'
import { ContentLoading } from '../../components/ContentLoading'
import apinode from '../../services/apinode'
import { sendErrorApiMessage } from '../../utils/sendErrorApiMessage'
import { formatDate } from '../../utils/formatDate'
import { formatPrice } from '../../utils/formatPrice'
import { formatCPF } from '../../utils/formatCPF'
import { unifyParams } from '../../utils/unifyParams'
import noDataPc from '../../assets/illustration/no-data-pc.svg'
import { CustomSpinner } from '../../components/CustomSpinner'
import { CustomButton } from '../../components/CustomButton'
import { CloudDownloadFill } from 'react-bootstrap-icons'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { MagnifyingGlass } from 'phosphor-react'

const Svg = (props) => (
  <svg
    width={props.size || 18}
    height={props.size || 18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    {props.children}
  </svg>
)

const IconRefresh = (p) => (
  <Svg {...p}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </Svg>
)

const IconCloud = (p) => (
  <Svg {...p}>
    <path d="M17.5 19a4.5 4.5 0 0 0 .5-9 6 6 0 0 0-11.6-1.5A4 4 0 0 0 6.5 19z" />
  </Svg>
)

const IconCloudCheck = (p) => (
  <Svg {...p}>
    <path d="M17.5 19a4.5 4.5 0 0 0 .5-9 6 6 0 0 0-11.6-1.5A4 4 0 0 0 6.5 19" />
    <path d="m9 15 2 2 4-4" />
  </Svg>
)

const IconBolt = (p) => (
  <Svg
    {...p}
    fill="currentColor"
    stroke="none"
  >
    <path d="M13 2 4.5 13.5H11l-1 8.5L19.5 10H13z" />
  </Svg>
)

const IconUser = (p) => (
  <Svg {...p}>
    <circle
      cx="12"
      cy="8"
      r="4"
    />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
  </Svg>
)

/* ================================================================== */
/*  Tokens + styled-components                                        */
/* ================================================================== */
const t = {
  surface: '#ffffff',
  surfaceMuted: '#f6f7f9',
  border: '#e9ebf0',
  borderStrong: '#d5d9e2',
  text: '#1f2430',
  textMuted: '#6b7280',
  textFaint: '#9aa1ad',
  accent: '#3483fa',
  radius: '12px',
  shadow: '0 1px 2px rgba(16,24,40,.04)',
  shadowHover: '0 4px 20px rgba(16,24,40,.10)',
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  h1 {
    margin: 0;
  }
  .count {
    color: ${t.textMuted};
    font-size: 13px;
  }
`

const Filters = styled(Row)`
  background: ${t.surfaceMuted};
  border-radius: ${t.radius};
  padding: 14px 6px;
  margin: 0;

  label {
    font-size: 12px;
    color: ${t.textMuted};
    margin-bottom: 4px;
  }
`

const SearchWrap = styled.div`
  display: flex;
  gap: 8px;
`

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  background: ${t.accent};
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.05s ease;

  &:hover {
    filter: brightness(1.06);
  }
  &:active {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const OrdersSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .pagination {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    list-style: none;
    padding: 0;
    margin: 24px 0 4px;

    li a {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 38px;
      height: 38px;
      padding: 0 10px;
      border-radius: 10px;
      border: 1px solid ${t.border};
      background: ${t.surface};
      color: ${t.text};
      font-size: 14px;
      cursor: pointer;
      transition: all 0.15s ease;
      user-select: none;
    }
    li a:hover {
      border-color: ${t.accent};
      color: ${t.accent};
    }
    li.active a {
      background: ${t.accent};
      border-color: ${t.accent};
      color: #fff;
      font-weight: 600;
    }
    li.disabled a {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;

  img {
    width: 380px;
    max-width: 100%;
    height: auto;
  }
  h2 {
    font-size: 18px;
    margin: 0;
  }
  span {
    color: ${t.textMuted};
    font-size: 14px;
  }
`

const Order = styled.article`
  background: #fff;
  border: 1px solid ${({ isOpen }) => (isOpen ? t.borderStrong : t.border)};
  border-radius: ${t.radius};
  box-shadow: ${({ isOpen }) => (isOpen ? t.shadowHover : t.shadow)};
  overflow: hidden;
  transition:
    box-shadow 0.18s ease,
    border-color 0.18s ease;

  &:hover {
    box-shadow: ${t.shadowHover};
    border-color: ${t.borderStrong};
  }
`

const SectionInfo = styled.header`
  display: grid;
  grid-template-columns: 170px 1fr auto 40px;
  align-items: center;
  gap: 18px;
  padding: 16px 18px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    gap: 12px;
  }
`

const StatusCol = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const OrderId = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;

  .head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    strong {
      font-size: 14px;
      font-weight: 600;
      color: ${t.text};
    }
    span {
      font-size: 14px;
      color: ${t.textMuted};
    }
  }
  .meta {
    font-size: 12px;
    color: ${t.textFaint};
  }
`

const Price = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${t.text};
  white-space: nowrap;
`

const SectionPlusInfo = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 16px;
  padding: 18px;
  border-top: 1px solid ${t.border};
  background: ${t.surfaceMuted};
  animation: ${fadeIn} 0.22s ease;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background: #fff;
  border: 1px solid ${t.border};
  border-radius: ${t.radius};
  padding: 14px;

  hr {
    border: none;
    border-top: 1px solid ${t.border};
    margin: 12px 0;
  }
`

const Item = styled(Card)`
  display: flex;
  flex-direction: column;

  img {
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 10px;
    border: 1px solid ${t.border};
    background: #fff;
    flex-shrink: 0;
  }
  hr:last-child {
    display: none;
  }
`

const User = styled(Card)``
const Total = styled(Card)`
  height: fit-content;
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 3px 0;
`

const Text = styled.p`
  margin: 0;
  font-size: ${({ strong }) => (strong ? '14px' : '12.5px')};
  font-weight: ${({ strong }) => (strong ? 600 : 400)};
  color: ${({ strong }) => (strong ? t.text : t.textMuted)};
  line-height: 1.55;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : '0')};
  word-break: break-word;
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  background: ${({ bg }) => bg || '#eef0f3'};
  color: ${({ color }) => color || '#5f6672'};
`

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : '0')};
`

/* ================================================================== */
/*  Componente                                                       */
/* ================================================================== */
export function MercadoLivrePedidos() {
  const [orders, setOrders] = useState([])
  const [accounts, setAccounts] = useState([])
  const [totalOrders, setTotalOrders] = useState(0)
  const [inputSearch, setInputSearch] = useState('')
  const [selectedOrder, setSelectedOrder] = useState('')
  const [selectedAccount, setSelectedAccount] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedDate, setSelectedDate] = useState()
  const [isLoadingOrders, setIsLoadingOrders] = useState(true)
  const [isLoadingOrder, setIsLoadingOrder] = useState({})
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true)
  const [itemsPerPage] = useState(51)
  const [itemOffset, setItemOffset] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [pageNumber, setPageNumber] = useState(0)
  const [isImportOrderLoading, setIsImportOrderLoading] = useState({})

  const totalItems = selectedOrder.items
    ? selectedOrder.items.reduce(
        (acc, item) => acc + item.unit_price * item.quantity,
        0,
      )
    : 0

  const totalItemsDiscount = selectedOrder.items
    ? selectedOrder.items.reduce(
        (acc, item) =>
          item?.sale_fee ? acc + item.sale_fee * item.quantity : acc,
        0,
      ) + selectedOrder.base_cost
    : 0

  const inputSearchRef = useRef(null)

  const statusMap = {
    confirmed: 'Confirmado',
    payment_required: 'Pagamento necessário',
    payment_in_process: 'Pagamento em processo',
    paid: 'Pago',
    cancelled: 'Cancelado',
    invalid: 'Inválido',
  }

  const statusStyleMap = {
    confirmed: { bg: '#E1F5EE', color: '#0F6E56' },
    payment_required: { bg: '#FAEEDA', color: '#854F0B' },
    payment_in_process: { bg: '#FAECE7', color: '#993C1D' },
    paid: { bg: '#EAF3DE', color: '#3B6D11' },
    cancelled: { bg: '#FCEBEB', color: '#A32D2D' },
    invalid: { bg: '#F1EFE8', color: '#5F5E5A' },
  }

  const statusMapArray = Object.entries(statusMap)

  async function handleImportOrder(order, index) {
    try {
      setIsImportOrderLoading((prevState) => ({ ...prevState, [index]: true }))

      // >>> Chamada de importação. Ajuste a rota conforme seu backend. <<<
      await apinode.post(`/ordersml/orders/import/${selectedAccount}`, {
        orderId: order.id,
      })

      // Marca o pedido como importado na lista sem precisar refazer o fetch
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, imported: true } : o)),
      )
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao importar esse pedido.')
    } finally {
      setIsImportOrderLoading((prevState) => ({ ...prevState, [index]: false }))
    }
  }

  async function getOrder(orderId, index) {
    try {
      setIsLoadingOrder((prevState) => ({ ...prevState, [index]: true }))

      const response = await apinode.get(
        `/ordersml/orders/${orderId}/${selectedAccount}`,
      )

      setSelectedOrder(response.data)
    } catch (err) {
      sendErrorApiMessage(
        err,
        'Não foi possível buscar as informações do pedido!',
      )
    } finally {
      setIsLoadingOrder((prevState) => ({ ...prevState, [index]: false }))
    }
  }

  const buscarPedidos = useCallback(async () => {
    try {
      if (selectedAccount) {
        setIsLoadingOrders(true)

        const url = `/ordersml/orders/${selectedAccount}`

        const urlParams = unifyParams(url, [
          { key: 'date', value: selectedDate },
          { key: 'status', value: selectedStatus },
          { key: 'offset', value: itemOffset },
          { key: 'search', value: inputSearch },
        ])

        const response = await apinode.get(urlParams)

        setPageNumber(response.data.paging.offset / response.data.paging.limit)
        setTotalOrders(response.data.paging.total)
        setPageCount(Math.ceil(response.data.paging.total / itemsPerPage))
        setOrders(response.data.results)
      }
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível buscar os pedidos!')
    } finally {
      setIsLoadingOrders(false)
    }
  }, [
    inputSearch,
    itemOffset,
    itemsPerPage,
    selectedAccount,
    selectedDate,
    selectedStatus,
  ])

  const buscarContas = useCallback(async () => {
    try {
      setIsLoadingAccounts(true)

      const response = await apinode.get(
        `/anuncios/contas/${localStorage.idconta}`,
      )

      const responseFormatted = response.data.sort((a, b) =>
        a.APELIDO.localeCompare(b.APELIDO),
      )

      setAccounts(responseFormatted)
      setSelectedAccount(responseFormatted[0]?.ID_CONTA ?? '')
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível buscar as contas!')
    } finally {
      setIsLoadingAccounts(false)
    }
  }, [])

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % totalOrders
    setItemOffset(newOffset)
  }

  const handleSearch = () => {
    setItemOffset(0)
    setInputSearch(inputSearchRef.current.value)
  }

  useEffect(() => {
    buscarContas()
  }, [buscarContas])

  useEffect(() => {
    buscarPedidos()
  }, [buscarPedidos])

  return (
    <>
      <Navbar />

      <div className="mt-page">
        <Header>
          <div>
            <h1>Pedidos Mercado Livre</h1>
            {!isLoadingOrders && (
              <span className="count">
                {totalOrders}{' '}
                {totalOrders === 1
                  ? 'pedido encontrado'
                  : 'pedidos encontrados'}
              </span>
            )}
          </div>

          <PrimaryButton
            onClick={buscarPedidos}
            disabled={isLoadingOrders}
          >
            {isLoadingOrders ? (
              <CustomSpinner size={16} />
            ) : (
              <IconRefresh size={16} />
            )}
            {isLoadingOrders ? 'Atualizando...' : 'Atualizar'}
          </PrimaryButton>
        </Header>

        <Filters className="g-3">
          <Col md={3}>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={selectedStatus}
              onChange={(e) => {
                setItemOffset(0)
                setSelectedStatus(e.target.value)
              }}
            >
              <option value="">Todos os status</option>
              {statusMapArray.map(([key, value]) => (
                <option
                  key={key}
                  value={key}
                >
                  {value}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Data da venda</Form.Label>
            <CustomInputDate
              selected={selectedDate}
              onChange={(date) => {
                setItemOffset(0)
                setSelectedDate(date)
              }}
              placeholderText="Data da venda"
              isClearable
            />
          </Col>

          <Col md={3}>
            <Form.Label>Busca</Form.Label>
            <SearchWrap>
              <Form.Control
                ref={inputSearchRef}
                placeholder="ID, comprador, nickname"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch()
                }}
              />

              <CustomButton
                onClick={handleSearch}
                isLoading={isLoadingOrders}
              >
                <MagnifyingGlass size={18} />
              </CustomButton>
            </SearchWrap>
          </Col>

          <Col md={3}>
            <Form.Label>Conta</Form.Label>
            <Form.Select
              value={selectedAccount}
              onChange={(e) => {
                setItemOffset(0)
                setSelectedAccount(e.target.value)
              }}
              disabled={isLoadingAccounts}
            >
              {accounts.map((conta) => (
                <option
                  key={conta.ID_CONTA}
                  value={conta.ID_CONTA}
                >
                  {conta.APELIDO}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Filters>

        <div className="mt-4">
          <ContentLoading
            isLoading={isLoadingAccounts}
            loadingText="Carregando contas..."
          >
            <ContentLoading
              isLoading={isLoadingOrders}
              loadingText="Carregando pedidos..."
            >
              {orders.length === 0 ? (
                <EmptyState>
                  <img
                    src={noDataPc}
                    alt=""
                  />
                  <h2>Nenhum pedido encontrado</h2>
                  <span>
                    Ajuste os filtros ou selecione outra conta para ver os
                    pedidos.
                  </span>
                </EmptyState>
              ) : (
                <OrdersSection>
                  {orders.map((order, index) => {
                    const isOpen = selectedOrder?.id === order.id
                    const statusStyle =
                      statusStyleMap[order.status] || statusStyleMap.invalid

                    return (
                      <Order
                        key={order.id}
                        isOpen={isOpen}
                      >
                        <SectionInfo>
                          <StatusCol>
                            <Badge
                              bg={statusStyle.bg}
                              color={statusStyle.color}
                            >
                              {statusMap[order.status]}
                            </Badge>

                            {order.fulfilled && (
                              <OverlayTrigger overlay={<Tooltip>Full</Tooltip>}>
                                <Badge
                                  bg="#E1F5EE"
                                  color="#0F6E56"
                                >
                                  <IconBolt size={13} />
                                  Full
                                </Badge>
                              </OverlayTrigger>
                            )}
                          </StatusCol>

                          <OrderId>
                            <div className="head">
                              <strong>#{order.id}</strong>
                              <span>· {order.customer}</span>

                              <OverlayTrigger
                                overlay={
                                  <Tooltip>
                                    {order.imported
                                      ? 'Importado'
                                      : 'Não importado'}
                                  </Tooltip>
                                }
                              >
                                <span
                                  style={{
                                    color: order.imported
                                      ? '#1D9E75'
                                      : '#A7A2A9',
                                    display: 'inline-flex',
                                  }}
                                >
                                  {order.imported ? (
                                    <IconCloudCheck size={18} />
                                  ) : (
                                    <IconCloud size={18} />
                                  )}
                                </span>
                              </OverlayTrigger>
                            </div>

                            <span className="meta">
                              {formatDate(
                                order.date_created,
                                'dd/MM/yyyy HH:mm',
                              )}{' '}
                              · {order.seller}
                            </span>

                            {!order.imported && (
                              <div style={{ marginTop: 6 }}>
                                <CustomButton
                                  isLoading={isImportOrderLoading[index]}
                                  isLoadingText="Importando..."
                                  noMargin
                                  title="Importar"
                                  onClick={() =>
                                    handleImportOrder(order, index)
                                  }
                                >
                                  <CloudDownloadFill size={14} />
                                </CustomButton>
                              </div>
                            )}
                          </OrderId>

                          <Price>{formatPrice(order.total_amount)}</Price>

                          {isLoadingOrder[index] ? (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                color: t.accent,
                              }}
                            >
                              <CustomSpinner size={20} />
                            </div>
                          ) : isOpen ? (
                            <CustomButton
                              aria-label="Recolher"
                              onClick={() => setSelectedOrder({})}
                            >
                              <ChevronUp
                                style={{
                                  width: 18,
                                  height: 18,
                                }}
                              />
                            </CustomButton>
                          ) : (
                            <CustomButton
                              aria-label="Expandir"
                              onClick={() => getOrder(order.id, index)}
                            >
                              <ChevronDown
                                style={{
                                  width: 18,
                                  height: 18,
                                }}
                              />
                            </CustomButton>
                          )}
                        </SectionInfo>

                        {isOpen && (
                          <SectionPlusInfo>
                            <Item>
                              {selectedOrder?.items.map((item, itemIndex) => (
                                <div key={item?.sku ?? itemIndex}>
                                  <div style={{ display: 'flex', gap: 12 }}>
                                    <img
                                      src={item?.thumbnail}
                                      alt=""
                                    />
                                    <div style={{ minWidth: 0 }}>
                                      <Text
                                        strong
                                        mb={2}
                                      >
                                        {item?.title}
                                      </Text>
                                      <Text>SKU: {item?.sku}</Text>
                                      <Text>
                                        {item?.quantity}{' '}
                                        {item?.quantity > 1
                                          ? 'unidades'
                                          : 'unidade'}{' '}
                                        · {formatPrice(item?.unit_price)}
                                      </Text>
                                      {item?.variation_attributes?.map(
                                        (attr, attrIndex) => (
                                          <Text key={attrIndex}>
                                            {attr?.name}: {attr?.value_name}
                                          </Text>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                  <hr />
                                </div>
                              ))}
                            </Item>

                            <User>
                              <InlineRow mb={10}>
                                <IconUser size={18} />
                                <Text strong>{selectedOrder?.customer}</Text>
                              </InlineRow>
                              <Text>
                                {selectedOrder?.shippingAddress?.street_name},{' '}
                                {selectedOrder?.shippingAddress?.street_number},{' '}
                                {selectedOrder?.shippingAddress?.neighborhood} —{' '}
                                {selectedOrder?.shippingAddress?.city}/
                                {selectedOrder?.shippingAddress?.state} · CEP:{' '}
                                {selectedOrder?.shippingAddress?.zipcode}
                              </Text>

                              <hr />

                              <Text
                                strong
                                mb={10}
                              >
                                Dados da fatura
                              </Text>

                              {selectedOrder?.billingAddress?.customer && (
                                <InlineRow mb={6}>
                                  <IconUser size={18} />
                                  <Text>
                                    {selectedOrder?.billingAddress?.customer}
                                  </Text>
                                </InlineRow>
                              )}

                              <Text>CPF: {formatCPF(selectedOrder?.cpf)}</Text>
                              <Text>
                                {selectedOrder?.billingAddress?.street_name},{' '}
                                {selectedOrder?.billingAddress?.street_number},{' '}
                                {selectedOrder?.billingAddress?.neighborhood} —{' '}
                                {selectedOrder?.billingAddress?.city}/
                                {selectedOrder?.billingAddress?.state} · CEP:{' '}
                                {selectedOrder?.billingAddress?.zipcode}
                              </Text>
                            </User>

                            <Total>
                              <Divider>
                                <Text>Produtos</Text>
                                <Text>{formatPrice(totalItems)}</Text>
                              </Divider>

                              {selectedOrder?.items.map((item, itemIndex) => {
                                const label = `Tarifa ${item.title}`
                                return (
                                  <Divider key={item?.sku ?? itemIndex}>
                                    <Text>
                                      {label.length > 40
                                        ? `${label.slice(0, 40)}…`
                                        : label}
                                    </Text>
                                    <Text>
                                      −
                                      {formatPrice(
                                        item.sale_fee * item.quantity,
                                      )}
                                    </Text>
                                  </Divider>
                                )
                              })}

                              <Divider>
                                <Text>Envio pelo Mercado Envios</Text>
                                <Text>
                                  −{formatPrice(selectedOrder?.base_cost)}
                                </Text>
                              </Divider>

                              <hr />

                              <Divider>
                                <Text strong>Total</Text>
                                <Text strong>
                                  {formatPrice(totalItems - totalItemsDiscount)}
                                </Text>
                              </Divider>
                            </Total>
                          </SectionPlusInfo>
                        )}
                      </Order>
                    )
                  })}

                  <ReactPaginate
                    breakLabel="..."
                    nextLabel="Próxima"
                    initialPage={pageNumber}
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={5}
                    pageCount={pageCount}
                    previousLabel="Anterior"
                    containerClassName="pagination"
                    activeClassName="active"
                  />
                </OrdersSection>
              )}
            </ContentLoading>
          </ContentLoading>
        </div>
      </div>
    </>
  )
}
